import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { loyaltyApi } from '@/api/loyalty'
import type { LoyaltyTierInput } from '@/types/ecommerce'

/** CrmService's loyalty program — tiers admin-managed here; members list is read-only
 * (membership comes from customer self-enrollment on the storefront). */
export function useLoyaltyTiersAdmin() {
  return useQuery({
    queryKey: ['loyalty', 'tiers'],
    queryFn: () => loyaltyApi.listTiers(),
  })
}

function invalidateTiers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['loyalty', 'tiers'] })
}

export function useCreateLoyaltyTier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: LoyaltyTierInput) => loyaltyApi.createTier(body),
    onSuccess: () => invalidateTiers(qc),
  })
}

export function useUpdateLoyaltyTier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LoyaltyTierInput }) => loyaltyApi.updateTier(id, body),
    onSuccess: () => invalidateTiers(qc),
  })
}

export function useDeleteLoyaltyTier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => loyaltyApi.removeTier(id),
    onSuccess: () => invalidateTiers(qc),
  })
}

export function useLoyaltyMembers() {
  return useQuery({
    queryKey: ['loyalty', 'members'],
    queryFn: () => loyaltyApi.listMembers(),
  })
}
